import cx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import Button from '@components/uikit/Button/Button';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useFileManagerUploadFileMutation } from '@services/useFileManagerUploadFileMutation';

import { selectAppRtl } from '@store/useAppStore';
import { useModalActions } from '@store/useModalStore';

import ModalHeader from '../ModalHeader/ModalHeader';
import Dropzone from './components/Dropzone/Dropzone';
import style from './style.module.scss';

const propTypes = {
  name: PropTypes.string,
  control: PropTypes.object,
  maxFilesCount: PropTypes.number,
  typeOfFiles: PropTypes.object,
  type: PropTypes.string,
  maxFileSize: PropTypes.number,
};

const AttachFilesModal = ({
  name,
  control,
  maxFilesCount,
  typeOfFiles = null,
  type = 'main',
  maxFileSize,
}) => {
  const { t } = useTranslation();
  const { customerId } = useParams();

  const rtl = selectAppRtl();
  const { setModalClose, setSecondaryModalClose } = useModalActions();

  const {
    field: { value: attachments, onChange },
  } = useController({ name, control });

  const [currentFiles, setCurrentFiles] = useState({
    files: [],
    uploadedFiles: [],
  });

  useEffect(() => {
    if (currentFiles.files.length > 0 && currentFiles.files.length === currentFiles.uploadedFiles.length) {
      onChange([...attachments, ...currentFiles.uploadedFiles]);
      handleClose();
    }
  }, [currentFiles]);

  const onFileManagerUploadFileMutateSuccess = (data) => {
    const currentFile = {
      path: data.file?.url,
      url: data.file?.url,
      name: data.file?.name + '.' + data.file?.extension,
      extension: data.file?.extension,
      size: data.file?.size,
      isUploaded: true,
    };

    setCurrentFiles((prevState) => ({
      ...prevState,
      uploadedFiles: [...prevState.uploadedFiles, currentFile],
    }));
  };

  const onFileManagerUploadFileMutateError = (variables) => {
    const filesArr = currentFiles.files.filter((file) => file.name !== variables.fileName);

    setCurrentFiles((prevState) => ({
      ...prevState,
      files: filesArr,
    }));
    showToast({ variant: 'success', message: t('Something went wrong!') });
  };

  const { mutate: fileManagerUploadFileMutate, isLoading: uploadFilesLoading } =
    useFileManagerUploadFileMutation({
      onSuccess: onFileManagerUploadFileMutateSuccess,
      onError: onFileManagerUploadFileMutateError,
    });

  const saveFiles = (filesArr) => {
    filesArr.forEach((file) => {
      const formData = new FormData();
      formData.append('file', file);

      fileManagerUploadFileMutate({ formData, customerId, fileName: file.name });
    });
  };

  const handleClose = () => {
    if (type === 'secondary') {
      setSecondaryModalClose();
    } else {
      setModalClose();
    }
    setCurrentFiles({
      files: [],
      uploadedFiles: [],
    });
  };

  const handleDrop = (filesArr) => {
    setCurrentFiles({
      files: filesArr,
      uploadedFiles: [],
    });
  };

  return (
    <div className={cx(style.attachFilesModal, { [style.bodyRTL]: rtl })}>
      <ModalHeader title={t('Add files')} variant="withoutSeparator" onClose={handleClose} />
      <div className={style.modalBody}>
        <div className={style.dropzoneWrapper}>
          <Dropzone
            maxFilesCount={maxFilesCount}
            typeOfFiles={typeOfFiles}
            currentFiles={currentFiles.files}
            maxFileSize={maxFileSize}
            onDrop={handleDrop}
          />
        </div>
      </div>
      <div className={style.modalFooter}>
        <Button type="secondary" text={t('Cancel')} disabled={uploadFilesLoading} onClick={handleClose} />
        <Button
          type="primary"
          variant={'medium'}
          text={t('Attach')}
          disabled={uploadFilesLoading}
          onClick={() => saveFiles(currentFiles.files)}
        />
      </div>
    </div>
  );
};

AttachFilesModal.propTypes = propTypes;

export default AttachFilesModal;
